import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { bookingApi, vehicleApi, customerApi, contractApi, stationApi, categoryApi, formatApiError } from '../services/api';
import CustomerAvatar from '../components/CustomerAvatar';
import AdditionalDriversFields from '../components/AdditionalDriversFields';
import { 
  Calendar, Plus, MagnifyingGlass, Funnel, Eye, 
  FileText, Trash, ArrowRight, Clock, CheckCircle, XCircle 
} from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import QuickCustomerCreate from '../components/QuickCustomerCreate';

const EXTRAS = [
  { id: 'gps', label: 'GPS Navigation', price: 5 },
  { id: 'kindersitz', label: 'Kindersitz', price: 10 },
  { id: 'zusatzfahrer', label: 'Zusatzfahrer', price: 15 },
  { id: 'vollkasko', label: 'Vollkasko-Versicherung', price: 20 },
  { id: 'winterreifen', label: 'Winterreifen', price: 8 }
];

const STATUS_LABELS = {
  confirmed: 'Bestätigt',
  active: 'Aktiv',
  completed: 'Abgeschlossen',
  cancelled: 'Storniert',
  terminated: 'Gekündigt'
};

const Bookings = ({ createNew = false }) => {
  const [bookings, setBookings] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [showModal, setShowModal] = useState(createNew);
  const navigate = useNavigate();
  const handleModalOpenChange = (open) => {
    setShowModal(open);
    if (!open && createNew) navigate('/bookings', { replace: true });
  };

  
  const [formData, setFormData] = useState({
    vehicle_id: '',
    customer_id: '',
    start_date: '',
    end_date: '',
    pickup_location: 'Büro',
    return_location: 'Büro',
    pickup_station_id: '',
    return_station_id: '',
    pickup_mileage: '',
    deposit_amount: '',
    insurance_deductible: '',
    use_weekend_rate: false,
    extras: [],
    notes: '',
    additional_drivers: [],
    manual_daily_rate: '',
    manual_base_price: '',
    manual_extras_price: '',
    manual_total_price: '',
    manual_price_note: '',
    included_km: '',
    extra_km_rate: '',
  });

  const [showQuickCustomer, setShowQuickCustomer] = useState(false);

  useEffect(() => {
    loadData();
  }, [filterStatus]);

  const loadData = async () => {
    try {
      const params = {};
      if (filterStatus && filterStatus !== 'all') params.status = filterStatus;
      
      const [bookingsRes, vehiclesRes, categoriesRes, customersRes, stationsRes] = await Promise.all([
        bookingApi.getAll(params),
        vehicleApi.getAll({ status: 'available' }),
        categoryApi.getAll(),
        customerApi.getAll(),
        stationApi.getAll()
      ]);
      
      setBookings(bookingsRes.data);
      setVehicles(vehiclesRes.data);
      setCategories(categoriesRes.data);
      setCustomers(customersRes.data);
      setStations(stationsRes.data);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        pickup_station_id: formData.pickup_station_id || null,
        return_station_id: formData.return_station_id || null,
        pickup_mileage: formData.pickup_mileage !== '' ? parseInt(formData.pickup_mileage) : null,
        deposit_amount: formData.deposit_amount !== '' ? parseFloat(formData.deposit_amount) : null,
        insurance_deductible: formData.insurance_deductible !== '' ? parseFloat(formData.insurance_deductible) : null,
        manual_daily_rate: formData.manual_daily_rate !== '' ? parseFloat(formData.manual_daily_rate) : null,
        manual_base_price: formData.manual_base_price !== '' ? parseFloat(formData.manual_base_price) : null,
        manual_extras_price: formData.manual_extras_price !== '' ? parseFloat(formData.manual_extras_price) : null,
        manual_total_price: formData.manual_total_price !== '' ? parseFloat(formData.manual_total_price) : null,
        manual_price_note: formData.manual_price_note || null,
        included_km: formData.included_km !== '' ? parseInt(formData.included_km) : null,
        extra_km_rate: formData.extra_km_rate !== '' ? parseFloat(formData.extra_km_rate) : null,
      };
      const response = await bookingApi.create(payload);
      toast.success('Buchung erstellt');
      setShowModal(false);
      resetForm();
      navigate(`/bookings/${response.data.id}`);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Buchung wirklich löschen?')) return;
    try {
      await bookingApi.delete(id);
      toast.success('Buchung gelöscht');
      loadData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const downloadContract = async (bookingId) => {
    try {
      const response = await contractApi.getPdf(bookingId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = response.headers?.['content-disposition'] || '';
      const serverFilename = disposition.match(/filename="?([^";]+)"?/i)?.[1];
      a.download = (serverFilename || `vertrag_${bookings.find(b => b.id === bookingId)?.contract_number || bookings.find(b => b.id === bookingId)?.booking_number || bookingId.substring(0, 8)}.pdf`).replace(/[\\/:*?"<>|]/g, '_');
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const resetForm = () => {
    setFormData({
      vehicle_id: '',
      customer_id: '',
      start_date: '',
      end_date: '',
      pickup_location: 'Büro',
      return_location: 'Büro',
      pickup_station_id: '',
      return_station_id: '',
      pickup_mileage: '',
      deposit_amount: '',
      insurance_deductible: '',
      extras: [],
      notes: '',
      additional_drivers: [],
      manual_daily_rate: '',
      manual_base_price: '',
      manual_extras_price: '',
      manual_total_price: '',
      manual_price_note: '',
      included_km: '',
      extra_km_rate: '',
    });
  };

  // Compute days between start_date and end_date for live km preview
  const computeDays = (start, end) => {
    if (!start || !end) return 0;
    try {
      const sd = new Date(start); const ed = new Date(end);
      const ms = ed.getTime() - sd.getTime();
      const days = Math.floor(ms / (1000 * 60 * 60 * 24)) + 1;
      return days > 0 ? days : 0;
    } catch { return 0; }
  };

  // When vehicle changes, prefill deposit & deductible from vehicle defaults (if not already set)
  const handleVehicleChange = (vehicleId) => {
    const veh = vehicles.find(v => v.id === vehicleId);
    const days = computeDays(formData.start_date, formData.end_date);
    setFormData(prev => ({
      ...prev,
      vehicle_id: vehicleId,
      pickup_mileage: veh?.current_mileage != null ? String(veh.current_mileage) : '',
      deposit_amount: prev.deposit_amount === '' && veh ? (veh.default_deposit || '') : prev.deposit_amount,
      insurance_deductible: prev.insurance_deductible === '' && veh ? (veh.insurance_deductible || '') : prev.insurance_deductible,
      // Auto-fill km fields from vehicle if not already set
      included_km: prev.included_km === '' && veh && veh.included_km_per_day && days > 0
        ? String(parseInt(veh.included_km_per_day) * days)
        : prev.included_km,
      extra_km_rate: prev.extra_km_rate === '' && veh && veh.extra_km_rate != null
        ? String(veh.extra_km_rate)
        : prev.extra_km_rate,
    }));
  };

  const toggleExtra = (extraId) => {
    setFormData(prev => ({
      ...prev,
      extras: prev.extras.includes(extraId)
        ? prev.extras.filter(e => e !== extraId)
        : [...prev.extras, extraId]
    }));
  };

  const getStatusBadge = (status) => {
    const styles = {
      confirmed: 'badge-warning',
      active: 'badge-success',
      completed: 'badge-neutral',
      cancelled: 'badge-error',
      terminated: 'badge-error'
    };
    return <span className={`badge ${styles[status] || 'badge-neutral'}`}>{STATUS_LABELS[status] || status}</span>;
  };

  const calculateEstimate = () => {
    if (!formData.vehicle_id || !formData.start_date || !formData.end_date) return null;

    const vehicle = vehicles.find(v => v.id === formData.vehicle_id);
    if (!vehicle) return null;

    const start = new Date(formData.start_date);
    const end = new Date(formData.end_date);
    const MS_DAY = 1000 * 60 * 60 * 24;
    const days = Math.max(1, Math.ceil((end - start) / MS_DAY));

    const category = categories.find(c => c.id === vehicle.category_id);

    // Preise kommen bevorzugt aus der Fahrzeugkategorie.
    // Fahrzeugpreise dienen als Fallback, falls keine Kategorie vorhanden ist.
    const dailyRate = Number(category?.daily_rate ?? vehicle.daily_rate ?? 0);
    const weeklyRate = Number(category?.weekly_rate ?? vehicle.weekly_rate ?? 0);
    const monthlyRate = Number(category?.monthly_rate ?? vehicle.monthly_rate ?? 0);
    const weekendRate = Number(category?.weekend_rate ?? vehicle.weekend_rate ?? 0);

    // Dynamische Tarifberechnung:
    // 28 Tage = Monat
    // 7 Tage = Woche
    // Freitag bis Montag = 3-Tage-Wochenende
    // übrige Tage = Tagessatz
    const dp = Array(days + 1).fill(null);
    dp[0] = { total: 0, components: [] };

    const addCandidate = (from, length, price, label) => {
      if (!price || !dp[from]) return;

      const to = from + length;
      if (to > days) return;

      const candidate = {
        total: dp[from].total + price,
        components: [
          ...dp[from].components,
          { length, price, label }
        ]
      };

      if (!dp[to] || candidate.total < dp[to].total) {
        dp[to] = candidate;
      }
    };

    for (let i = 0; i < days; i++) {
      if (!dp[i]) continue;

      if (monthlyRate > 0) {
        addCandidate(
          i,
          28,
          monthlyRate,
          `1 × Monatspreis (${monthlyRate.toFixed(2)} €)`
        );
      }

      if (weeklyRate > 0) {
        addCandidate(
          i,
          7,
          weeklyRate,
          `1 × Wochenpreis (${weeklyRate.toFixed(2)} €)`
        );
      }

      // Freitag → Montag = 3 Miettage
      if (weekendRate > 0) {
        const blockDate = new Date(start.getTime() + i * MS_DAY);

        if (blockDate.getDay() === 5 && i + 3 <= days) {
          addCandidate(
            i,
            3,
            weekendRate,
            `1 × Wochenendpreis Fr–Mo (${weekendRate.toFixed(2)} €)`
          );
        }
      }

      if (dailyRate > 0) {
        addCandidate(
          i,
          1,
          dailyRate,
          `1 × Tag (${dailyRate.toFixed(2)} €)`
        );
      }
    }

    const breakdown = dp[days] || {
      total: days * dailyRate,
      components: [
        {
          length: days,
          price: days * dailyRate,
          label: `${days} Tage × ${dailyRate.toFixed(2)} €`
        }
      ]
    };

    const basePrice = Number(breakdown.total.toFixed(2));

    const extrasPrice = formData.extras.reduce((sum, extraId) => {
      const extra = EXTRAS.find(e => e.id === extraId);
      return sum + (extra ? extra.price * days : 0);
    }, 0);

    return {
      days,
      basePrice,
      extrasPrice,
      total: Number((basePrice + extrasPrice).toFixed(2)),
      breakdown: breakdown.components
    };
  };

  const estimate = calculateEstimate();

  return (
    <div className="p-6 space-y-6" data-testid="bookings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#111418]">Buchungen</h1>
          <p className="text-[#525866] mt-1">{bookings.length} Buchungen insgesamt</p>
        </div>
        <Button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="btn-primary"
          data-testid="new-booking-btn"
        >
          <Plus weight="bold" className="mr-2" size={18} />
          Neue Buchung
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-48 input-swiss" data-testid="booking-status-filter">
            <Funnel className="mr-2" size={16} />
            <SelectValue placeholder="Alle Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Status</SelectItem>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Bookings List */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="card-swiss h-24 skeleton" />
          ))}
        </div>
      ) : bookings.length === 0 ? (
        <div className="card-swiss p-12 text-center">
          <Calendar weight="thin" size={64} className="mx-auto text-[#8A94A6] mb-4" />
          <p className="text-[#525866]">Keine Buchungen gefunden</p>
        </div>
      ) : (
        <div className="card-swiss overflow-hidden">
          <table className="table-swiss">
            <thead>
              <tr>
                <th>Kunde</th>
                <th>Fahrzeug</th>
                <th>Zeitraum</th>
                <th>Preis</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((booking) => (
                <tr key={booking.id} data-testid={`booking-row-${booking.id}`}>
                  <td>
                    <div className="flex items-center gap-2">
                      <CustomerAvatar customer={booking.customer} size={28} />
                      <p className="font-semibold text-[#111418]">
                        {booking.customer?.first_name} {booking.customer?.last_name}
                      </p>
                    </div>
                  </td>
                  <td>
                    <p className="text-[#525866]">
                      {booking.vehicle?.brand} {booking.vehicle?.model}
                    </p>
                    <p className="text-xs text-[#8A94A6]">{booking.vehicle?.license_plate}</p>
                  </td>
                  <td>
                    <p className="text-sm">
                      {new Date(booking.start_date).toLocaleDateString('de-DE')}
                    </p>
                    <p className="text-sm">
                      bis {new Date(booking.end_date).toLocaleDateString('de-DE')}
                    </p>
                  </td>
                  <td>
                    <p className="font-semibold">€{(booking.invoice_total ?? booking.price_info?.total_price ?? 0).toFixed(2)}</p>
                    <p className="text-xs text-[#8A94A6]">{booking.price_info?.days} Tage</p>
                  </td>
                  <td>{getStatusBadge(booking.status)}</td>
                  <td>
                    <div className="flex gap-2">
                      <Link to={`/bookings/${booking.id}`}>
                        <Button variant="ghost" size="sm" data-testid={`view-booking-${booking.id}`}>
                          <Eye weight="bold" size={16} />
                        </Button>
                      </Link>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => downloadContract(booking.id)}
                        data-testid={`download-contract-${booking.id}`}
                      >
                        <FileText weight="bold" size={16} />
                      </Button>
                      {booking.status !== 'active' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(booking.id)}
                          className="text-[#E60000] hover:bg-red-50"
                        >
                          <Trash weight="bold" size={16} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* New Booking Modal */}
      <Dialog open={showModal} onOpenChange={handleModalOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Neue Buchung</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="overline">Kunde</Label>
                <button type="button" onClick={() => setShowQuickCustomer(true)} className="text-xs text-[#E60000] hover:underline flex items-center gap-1" data-testid="quick-add-customer-booking">
                  <Plus weight="bold" size={12} /> Neuer Kunde
                </button>
              </div>
              <Select 
                value={formData.customer_id} 
                onValueChange={(v) => setFormData({...formData, customer_id: v})}
              >
                <SelectTrigger className="input-swiss" data-testid="booking-customer-select">
                  <SelectValue placeholder="Kunde auswählen" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.first_name} {c.last_name} {c.email ? `- ${c.email}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="overline">Fahrzeug</Label>
              <Select 
                value={formData.vehicle_id} 
                onValueChange={handleVehicleChange}
              >
                <SelectTrigger className="input-swiss" data-testid="booking-vehicle-select">
                  <SelectValue placeholder="Fahrzeug auswählen" />
                </SelectTrigger>
                <SelectContent>
                  {vehicles.map(v => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.brand} {v.model} ({v.license_plate}) - €{v.daily_rate}/Tag
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="overline">Von</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                  className="input-swiss"
                  min={new Date().toISOString().split('T')[0]}
                  required
                  data-testid="booking-start-date"
                />
              </div>
              <div>
                <Label className="overline">Bis</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                  className="input-swiss"
                  min={formData.start_date || new Date().toISOString().split('T')[0]}
                  required
                  data-testid="booking-end-date"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="overline">Abholstation</Label>
                <Select
                  value={formData.pickup_station_id}
                  onValueChange={(v) => setFormData({...formData, pickup_station_id: v, return_station_id: formData.return_station_id || v})}
                >
                  <SelectTrigger className="input-swiss" data-testid="booking-pickup-station">
                    <SelectValue placeholder={stations.length === 0 ? 'Erst Station anlegen' : 'Station wählen'} />
                  </SelectTrigger>
                  <SelectContent>
                    {stations.map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name} {s.city ? `- ${s.city}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline">Rückgabestation</Label>
                <Select
                  value={formData.return_station_id}
                  onValueChange={(v) => setFormData({...formData, return_station_id: v})}
                >
                  <SelectTrigger className="input-swiss" data-testid="booking-return-station">
                    <SelectValue placeholder={stations.length === 0 ? 'Erst Station anlegen' : 'Station wählen'} />
                  </SelectTrigger>
                  <SelectContent>
                    {stations.map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name} {s.city ? `- ${s.city}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="overline">Km-Stand Übergabe</Label>
                <Input
                  type="number"
                  value={formData.pickup_mileage}
                  onChange={(e) => setFormData({...formData, pickup_mileage: e.target.value})}
                  className="input-swiss"
                  placeholder="z. B. 45230"
                  min={0}
                  data-testid="booking-pickup-mileage"
                />
              </div>
              <div>
                <Label className="overline">Kaution (€)</Label>
                <Input
                  type="number"
                  value={formData.deposit_amount}
                  onChange={(e) => setFormData({...formData, deposit_amount: e.target.value})}
                  className="input-swiss"
                  placeholder="Default aus Fahrzeug"
                  min={0}
                  step={0.01}
                  data-testid="booking-deposit"
                />
              </div>
              <div>
                <Label className="overline">Selbstbehalt Vollkasko (€)</Label>
                <Input
                  type="number"
                  value={formData.insurance_deductible}
                  onChange={(e) => setFormData({...formData, insurance_deductible: e.target.value})}
                  className="input-swiss"
                  placeholder="Default aus Fahrzeug"
                  min={0}
                  step={0.01}
                  data-testid="booking-deductible"
                />
              </div>
            </div>

            <div className="p-3 bg-[#F7F8FA] border border-[#E2E4E9] rounded-sm">
              <Label className="overline mb-2 block">Kilometer-Pauschale</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-[#525866]">Inklusivkilometer (gesamt)</Label>
                  <Input
                    type="number"
                    value={formData.included_km}
                    onChange={(e) => setFormData({...formData, included_km: e.target.value})}
                    className="input-swiss"
                    placeholder="z. B. 1500 (leer = unbegrenzt)"
                    min={0}
                    step={1}
                    data-testid="booking-included-km"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#525866]">€ je Mehrkilometer</Label>
                  <Input
                    type="number"
                    value={formData.extra_km_rate}
                    onChange={(e) => setFormData({...formData, extra_km_rate: e.target.value})}
                    className="input-swiss"
                    placeholder="z. B. 0.30"
                    min={0}
                    step={0.01}
                    data-testid="booking-extra-km-rate"
                  />
                </div>
              </div>
              <p className="text-xs text-[#8A94A6] mt-2">
                Wird automatisch aus den Fahrzeug-Stammdaten vorbefüllt (Inklusivkilometer/Tag × Mietdauer). Manuell überschreibbar.
              </p>
            </div>

            <div>
              <Label className="overline mb-2 block">Extras</Label>
              <div className="grid grid-cols-2 gap-2">
                {EXTRAS.map(extra => (
                  <label
                    key={extra.id}
                    className={`flex items-center gap-3 p-3 border rounded-sm cursor-pointer transition-all ${
                      formData.extras.includes(extra.id) 
                        ? 'border-[#E60000] bg-red-50' 
                        : 'border-[#E2E4E9] hover:bg-[#F7F8FA]'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.extras.includes(extra.id)}
                      onChange={() => toggleExtra(extra.id)}
                      className="accent-[#E60000]"
                    />
                    <div>
                      <p className="text-sm font-medium">{extra.label}</p>
                      <p className="text-xs text-[#8A94A6]">+€{extra.price}/Tag</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <AdditionalDriversFields customers={customers} mainCustomerId={formData.customer_id} value={formData.additional_drivers} onChange={(drivers) => setFormData({ ...formData, additional_drivers: drivers })} />

            <div>
              <Label className="overline">Notizen</Label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="input-swiss w-full px-3 py-2 min-h-[80px]"
                rows={3}
              />
            </div>

            {/* Manuelle Preis-Overrides */}
            <details className="p-3 bg-[#F7F8FA] border border-[#E2E4E9] rounded-sm">
              <summary className="cursor-pointer overline">Preis manuell anpassen (optional)</summary>
              <div className="mt-3 space-y-3">
                <p className="text-xs text-[#8A94A6]">
                  Felder leer lassen für automatische Berechnung. Gesamtpreis überschreibt alle anderen Werte.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-[#8A94A6]">Tagespreis (€)</Label>
                    <Input
                      type="number" step="0.01" min={0}
                      value={formData.manual_daily_rate}
                      onChange={(e) => setFormData({ ...formData, manual_daily_rate: e.target.value })}
                      placeholder="leer = auto"
                      className="input-swiss"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-[#8A94A6]">Grundpreis gesamt (€)</Label>
                    <Input
                      type="number" step="0.01" min={0}
                      value={formData.manual_base_price}
                      onChange={(e) => setFormData({ ...formData, manual_base_price: e.target.value })}
                      placeholder="leer = auto"
                      className="input-swiss"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-[#8A94A6]">Extras-Preis (€)</Label>
                    <Input
                      type="number" step="0.01" min={0}
                      value={formData.manual_extras_price}
                      onChange={(e) => setFormData({ ...formData, manual_extras_price: e.target.value })}
                      placeholder="leer = auto"
                      className="input-swiss"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-[#8A94A6]">Gesamtpreis Brutto (€)</Label>
                    <Input
                      type="number" step="0.01" min={0}
                      value={formData.manual_total_price}
                      onChange={(e) => setFormData({ ...formData, manual_total_price: e.target.value })}
                      placeholder="leer = auto"
                      className="input-swiss font-semibold"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-[#8A94A6]">Begründung/Notiz</Label>
                  <Input
                    value={formData.manual_price_note}
                    onChange={(e) => setFormData({ ...formData, manual_price_note: e.target.value })}
                    placeholder="z.B. Stammkundenrabatt"
                    className="input-swiss"
                  />
                </div>
              </div>
            </details>

            {/* Price Estimate */}
            {estimate && (
              <div className="p-4 bg-[#F7F8FA] border border-[#E2E4E9] rounded-sm">
                <p className="overline mb-2">Preisschätzung</p>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Grundpreis ({estimate.days} Tage)</span>
                    <span>€{estimate.basePrice.toFixed(2)}</span>
                  </div>
                  {estimate.extrasPrice > 0 && (
                    <div className="flex justify-between">
                      <span>Extras</span>
                      <span>€{estimate.extrasPrice.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-base pt-2 border-t border-[#E2E4E9]">
                    <span>Gesamt</span>
                    <span className="text-[#E60000]">€{estimate.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleModalOpenChange(false)}>
                Abbrechen
              </Button>
              <Button 
                type="submit" 
                className="btn-primary" 
                disabled={!formData.vehicle_id || !formData.customer_id || !formData.start_date || !formData.end_date}
                data-testid="create-booking-btn"
              >
                Buchung erstellen
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <QuickCustomerCreate
        open={showQuickCustomer}
        onOpenChange={setShowQuickCustomer}
        onCreated={(newCustomer) => {
          setCustomers(prev => [...prev, newCustomer]);
          setFormData(prev => ({ ...prev, customer_id: newCustomer.id }));
        }}
      />
    </div>
  );
};

export default Bookings;
